import { Fragment } from 'react';
import { Link } from 'react-router';
import { Icon } from './Icon.js';

export interface FlowStep {
  name: string;
  /** One short line on what happens here. */
  note?: string;
  /** The node the reader is looking at. Inverted to ink. At most one. */
  current?: boolean;
  /** Spec 256: where this stage lives. The whole node becomes a link. */
  to?: string;
}

/**
 * Kridt's Flow diagram (hub-style-guide FlowDiagram, fda70d2). Spec 250.
 *
 * For explaining, not editing: equal nodes in one hairline row, the loop said
 * in words under it rather than drawn as a loose arrow. Five nodes at most;
 * more than that is a canvas. Below 720px the row stacks.
 *
 * Spec 256: a step with `to` is a link — the whole node — so a cycle drawn
 * here is also the way round it. `compact` is the form that sits under a page
 * title: names only, no notes, no loop line, and its row never stacks.
 */
export function FlowDiagram({
  steps,
  label,
  caption,
  loopsTo,
  testId,
  compact = false,
}: {
  steps: readonly FlowStep[];
  /** Names the diagram for screen readers. */
  label: string;
  /** Says what the reader should take from it. */
  caption?: string;
  /** For a cycle: where it goes back to, said in words under the row. */
  loopsTo?: string;
  testId?: string;
  compact?: boolean;
}) {
  return (
    <figure className={`hub-flow-figure${compact ? ' hub-flow-figure--compact' : ''}`} data-testid={testId}>
      <ol className={`hub-flow${compact ? ' hub-flow--compact' : ''}`} aria-label={label}>
        {steps.map((s, i) => {
          const body = (
            <>
              <span className="hub-flow__name">{s.name}</span>
              {s.note && !compact ? <span className="hub-flow__note">{s.note}</span> : null}
            </>
          );
          return (
            <Fragment key={s.name}>
              {i > 0 ? <li className="hub-flow__edge" aria-hidden="true" /> : null}
              <li
                className={`hub-flow__node${s.to ? ' hub-flow__node--link' : ''}`}
                aria-current={s.current ? 'step' : undefined}
              >
                {s.to ? (
                  <Link to={s.to} className="hub-flow__link">
                    {body}
                  </Link>
                ) : (
                  body
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
      {loopsTo && !compact ? (
        <p className="hub-flow__loop">
          <Icon name="bx-revision" />
          Then back to {loopsTo}
        </p>
      ) : null}
      {caption && !compact ? <figcaption className="hub-flow__caption">{caption}</figcaption> : null}
    </figure>
  );
}
